import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Calendar } from "lucide-react";
import { useEffect } from "react";

import { Link } from "@/components/link";
import {
  allCountKeys,
  allRequestListKeys,
  invalidateQueryKeys,
} from "@/hooks/queryKeys";
import { usePageTitle } from "@/hooks/usePageTitle";

const CALENDAR_URL = "https://www.nlc.com/calendars/kickstarterdemo";

function KickstarterDemoCalendarEmbed() {
  usePageTitle("Kickstarter Call");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === "calendar.booking_completed" ||
        event.data?.meetingBookSucceeded
      ) {
        queryClient.invalidateQueries({ queryKey: ["user"] });
        invalidateQueryKeys(queryClient, [
          ...allRequestListKeys,
          ...allCountKeys,
        ]);
        navigate({ search: { filter: "all", tab: "active" }, to: "/requests" });
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [navigate, queryClient]);

  return (
    <div className="min-h-screen bg-bg-secondary">
      <div className="max-w-full">
        <div className="flex gap-xl rounded-xl border border-[#D0D5DD] bg-white mb-4 p-xl max-w-[1000px] mx-auto">
          <div className="grid size-5xl shrink-0 place-items-center rounded-full bg-bg-brand-secondary">
            <Calendar className="text-featured-icon-light-fg-brand" size={20} />
          </div>
          <div className="flex flex-1 flex-col gap-xs">
            <span className="font-semibold text-lg text text-text-brand-secondary">
              Set Yourself Up for Success with a Quick Onboarding Call
            </span>
            <span className="font-normal text-sm text-text-tertiary">
              Booking an onboarding call ensures you get the most out of your
              design subscription by learning how to submit briefs effectively
              and achieve the best results. This quick session sets you up for
              success, helping streamline your workflow and maximize the impact
              of every design project.
            </span>
            <span className="font-bold text-sm text-button-tertiary-fg">
              See{" "}
              <Link
                className="text-button-tertiary-color-fg"
                href="https://help.nlc.com/en/articles/10750963-onboarding-at-nlc-everything-you-need-to-know"
                rel="noopener noreferrer"
                target="_blank"
              >
                everything you need to know
              </Link>{" "}
              about our plans.
            </span>
          </div>
        </div>
        <iframe
          className="w-full mx-auto max-w-[1000px] block"
          src={CALENDAR_URL}
          style={{ border: "none", minHeight: "700px" }}
          title="Schedule a Kickstarter Demo Call"
        />
      </div>
    </div>
  );
}

export const Route = createFileRoute("/kickstarter-call")({
  component: KickstarterDemoCalendarEmbed,
});
